import React, { useState, useEffect } from "react";
import { 
  Box, 
  Typography, 
  Button, 
  Container, 
  TextField,
  Alert,
  CircularProgress 
} from "@mui/material";
import { GlowingEffect } from "../ui/glowing-effect";
import { API_URL } from "../../globals";
import axios from "axios";

const DemoSuccessStep = ({ onComplete }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      // Get test cases, app URL, and login notes from localStorage
      const testCases = JSON.parse(localStorage.getItem('onboardingTestCases') || '[]');
      const appUrl = localStorage.getItem('onboardingAppUrl') || '';
      const loginNotes = localStorage.getItem('onboardingLoginNotes') || '';
      
      // Send email, test cases, app URL, and login notes to playwright_project endpoint
      const response = await axios.post(`${API_URL}/test/playwright_project`, {
        email: email.trim(),
        test_cases: testCases,
        app_url: appUrl,
        login_notes: loginNotes,
        is_demo: true
      }, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      console.log('Demo submission successful:', response.data);
      setIsSubmitted(true);
      
      // Wait a moment to show success, then complete
      setTimeout(() => {
        onComplete();
      }, 2000);

    } catch (error) {
      console.error('Error submitting demo:', error);
      setError(
        error.response?.data?.message || 
        'Failed to submit demo. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <Container 
        maxWidth={false}
        sx={{ 
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 4, sm: 6, md: 8 },
          overflow: "hidden",
          maxHeight: "100vh",
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            maxWidth: "600px",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: { xs: 4, sm: 6 },
            height: "100%",
            maxHeight: "100%",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              borderRadius: "20px",
              border: "1px solid rgba(76, 175, 80, 0.3)",
              padding: "8px",
              animation: "fadeInScale 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
              "@keyframes fadeInScale": {
                "0%": {
                  opacity: 0,
                  transform: "scale(0.9)",
                },
                "100%": {
                  opacity: 1,
                  transform: "scale(1)",
                },
              },
            }}
          >
            <Box
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 4,
                overflow: "hidden",
                borderRadius: "12px",
                padding: { xs: 4, sm: 5, md: 6 },
                background: "rgba(0, 0, 0, 0.4)",
                backdropFilter: "blur(10px)",
                boxShadow: "0 0 20px rgba(76, 175, 80, 0.3)",
              }}
            >
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: "#ffffff",
                  letterSpacing: "-0.04em",
                  lineHeight: 1.1,
                  fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                  mb: 2,
                }}
              >
                ✅ Your Tests Are Being Generated!
              </Typography>
              
              <Typography
                variant="body1"
                sx={{
                  color: "#e2e8f0",
                  fontWeight: 400,
                  lineHeight: 1.6,
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                  textAlign: "center",
                }}
              >
                Success! You'll receive your generated Playwright tests via email in 1-3 hours - depending on current queue and the length of your tests.
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: "#e2e8f0",
                  fontWeight: 400,
                  lineHeight: 1.6,
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                  textAlign: "center",
                  mb: 2,
                }}
              >
                In the meantime, you can learn more or go ahead and sign up for the full platform.
              </Typography>

              <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" }, width: "100%", maxWidth: "400px" }}>
                <Button
                  variant="outlined"
                  onClick={() => window.open('https://fumedev.com', '_blank')}
                  sx={{
                    flex: 1,
                    height: 48,
                    borderRadius: 2,
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    color: "#ffffff",
                    fontSize: "1rem",
                    fontWeight: 500,
                    textTransform: "none",
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                    transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      border: "1px solid rgba(255, 255, 255, 0.4)",
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                    },
                  }}
                >
                  Learn More
                </Button>
                <Button
                  variant="contained"
                  onClick={() => window.location.href = '/signup'}
                  sx={{
                    flex: 1,
                    height: 48,
                    borderRadius: 2,
                    background: "#ffffff",
                    border: "none",
                    color: "#000000",
                    fontSize: "1rem",
                    fontWeight: 600,
                    textTransform: "none",
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                    boxShadow: "0 4px 16px rgba(255, 255, 255, 0.2), 0 2px 8px rgba(255, 255, 255, 0.1)",
                    transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
                    "&:hover": {
                      transform: "translateY(-2px) scale(1.02)",
                      boxShadow: "0 8px 24px rgba(255, 255, 255, 0.25), 0 4px 12px rgba(255, 255, 255, 0.15)",
                      background: "#f8fafc",
                    },
                    "&:active": {
                      transform: "translateY(0) scale(1)",
                      transition: "all 0.1s cubic-bezier(0.16, 1, 0.3, 1)",
                    },
                  }}
                >
                  Sign Up
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth={false}
      sx={{ 
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 4, sm: 6, md: 8 },
        overflow: "hidden",
        maxHeight: "100vh",
      }}
    >
      {/* Main content */}
      <Box
        sx={{
          textAlign: "center",
          maxWidth: "600px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: { xs: 4, sm: 6 },
          height: "100%",
          maxHeight: "100%",
          overflow: "hidden",
        }}
      >
        {/* Success Message Card */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            borderRadius: "20px",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            padding: "8px",
            animation: showContent ? "fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.2s both" : "none",
            "@keyframes fadeInUp": {
              "0%": {
                opacity: 0,
                transform: "translateY(24px)",
              },
              "100%": {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          }}
        >
          <GlowingEffect
            spread={32}
            glow={true}
            disabled={false}
            proximity={48}
            inactiveZone={0.01}
            borderWidth={1}
          />
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: 4,
              overflow: "hidden",
              borderRadius: "12px",
              padding: { xs: 4, sm: 5, md: 6 },
              background: "rgba(0, 0, 0, 0.4)",
              backdropFilter: "blur(10px)",
            }}
          >
            {/* Title */}
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontWeight: 700,
                color: "#ffffff",
                letterSpacing: "-0.04em",
                lineHeight: 1.1,
                fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                mb: 2,
              }}
            >
              📧 Get Your Tests
            </Typography>
            
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, textAlign: "center", mb: 2 }}>
              <Typography
                variant="body1"
                sx={{
                  color: "#e2e8f0",
                  fontWeight: 400,
                  lineHeight: 1.6,
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                }}
              >
                Your tests are being generated. We need your email to send them to you when they are ready. <strong>Promise we will not use this email for marketing spam.</strong>
              </Typography>

            </Box>

            {/* Email Input */}
            <Box sx={{ width: "100%", maxWidth: "400px" }}>
              <TextField
                fullWidth
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    borderRadius: 2,
                    fontSize: "1.1rem",
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                    "& fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.2)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.4)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#4a90e2",
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    color: "#ffffff",
                    padding: "16px",
                    "&::placeholder": {
                      color: "rgba(255, 255, 255, 0.5)",
                      opacity: 1,
                    },
                  },
                }}
              />

              {error && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 2,
                    backgroundColor: "rgba(244, 67, 54, 0.1)",
                    color: "#f44336",
                    "& .MuiAlert-icon": {
                      color: "#f44336"
                    }
                  }}
                >
                  {error}
                </Alert>
              )}
            </Box>

            {/* CTA Button */}
            <Box
              sx={{
                width: "100%",
                maxWidth: "400px",
              }}
            >
              <Button
                fullWidth
                type="submit"
                disabled={isSubmitting}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                sx={{
                  height: 56,
                  borderRadius: 2,
                  background: isSubmitting ? "rgba(255, 255, 255, 0.6)" : "#ffffff",
                  border: "none",
                  color: "#000000",
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  textTransform: "none",
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                  boxShadow: "0 4px 16px rgba(255, 255, 255, 0.2), 0 2px 8px rgba(255, 255, 255, 0.1)",
                  transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
                  "&:hover": {
                    transform: isSubmitting ? "none" : "translateY(-2px) scale(1.02)",
                    boxShadow: isSubmitting ? "0 4px 16px rgba(255, 255, 255, 0.2), 0 2px 8px rgba(255, 255, 255, 0.1)" : "0 8px 24px rgba(255, 255, 255, 0.25), 0 4px 12px rgba(255, 255, 255, 0.15)",
                    background: isSubmitting ? "rgba(255, 255, 255, 0.6)" : "#f8fafc",
                  },
                  "&:active": {
                    transform: isSubmitting ? "none" : "translateY(0) scale(1)",
                    transition: "all 0.1s cubic-bezier(0.16, 1, 0.3, 1)",
                  },
                  "&.Mui-disabled": {
                    color: "rgba(0, 0, 0, 0.6)",
                  }
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <CircularProgress size={20} sx={{ color: "#000000" }} />
                      <span>Sending Results...</span>
                    </>
                  ) : (
                    <>
                      <span>Send My Demo Results</span>
                      <Box
                        sx={{
                          transition: "transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
                          transform: isHovered ? "translateX(4px)" : "translateX(0)",
                          fontSize: "1.2rem",
                        }}
                      >
                        →
                      </Box>
                    </>
                  )}
                </Box>
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default DemoSuccessStep; 